/**
 * Cross-tab communication utility using localStorage events
 * Provides a simple way to communicate between browser tabs without WebSockets
 */

export interface CrossTabEvent {
  type: string
  data: any
  timestamp: number
  tabId: string
}

export interface CrossTabListener {
  type: string
  callback: (data: any, event: CrossTabEvent) => void
}

class CrossTabCommunication {
  private listeners: CrossTabListener[] = []
  private tabId: string
  private storageKey = 'qse-cross-tab-events'

  constructor() {
    // Generate unique tab ID
    this.tabId = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    // Listen for storage events from other tabs
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.handleStorageEvent.bind(this))
    }
  }

  /**
   * Send an event to all other tabs
   */
  public sendEvent(type: string, data: any): void {
    if (typeof window === 'undefined') return

    const event: CrossTabEvent = {
      type,
      data,
      timestamp: Date.now(),
      tabId: this.tabId,
    }

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(event))
      // Clear the item immediately to trigger storage event
      localStorage.removeItem(this.storageKey)
    } catch (error) {
      console.warn('Failed to send cross-tab event:', error)
    }
  }

  /**
   * Listen for specific event types from other tabs
   */
  public addListener(
    type: string,
    callback: (data: any, event: CrossTabEvent) => void,
  ): () => void {
    const listener: CrossTabListener = { type, callback }

    this.listeners.push(listener)

    // Return cleanup function
    return () => {
      const index = this.listeners.findIndex(l => l === listener)

      if (index > -1) {
        this.listeners.splice(index, 1)
      }
    }
  }

  /**
   * Handle storage events from other tabs
   */
  private handleStorageEvent(event: StorageEvent): void {
    if (event.key !== this.storageKey || !event.newValue) return

    try {
      const crossTabEvent: CrossTabEvent = JSON.parse(event.newValue)

      // Ignore events from the same tab
      if (crossTabEvent.tabId === this.tabId) return

      // Notify listeners for this event type
      this.listeners
        .filter(listener => listener.type === crossTabEvent.type)
        .forEach(listener => {
          try {
            listener.callback(crossTabEvent.data, crossTabEvent)
          } catch (error) {
            console.warn('Error in cross-tab event listener:', error)
          }
        })
    } catch (error) {
      console.warn('Failed to parse cross-tab event:', error)
    }
  }

  /**
   * Clean up listeners and event handlers
   */
  public destroy(): void {
    this.listeners = []
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.handleStorageEvent.bind(this))
    }
  }
}

// Create singleton instance
const crossTabCommunication = new CrossTabCommunication()

// Export convenience functions
export const sendCrossTabEvent = (type: string, data: any) => {
  crossTabCommunication.sendEvent(type, data)
}

export const addCrossTabListener = (
  type: string,
  callback: (data: any, event: CrossTabEvent) => void,
) => {
  return crossTabCommunication.addListener(type, callback)
}

export const destroyCrossTabCommunication = () => {
  crossTabCommunication.destroy()
}

// Export the class for advanced usage
export { CrossTabCommunication }
